import classNames from 'classnames'
import * as React from 'react'

import { Button, ButtonSizeType, ButtonThemeType } from '../button'
import { ButtonGroup, ButtonGroupProps } from '../button-group'
import { SelectItem } from '../select/types'

export interface ButtonSwitchProps<ValueType> extends ButtonGroupProps {
  /**
   * Label to display above the select
   */
  label?: string

  /**
   * Class names for label
   */
  labelClassNames?: string

  /**
   * Array of items to display as button options
   */
  items: Array<Pick<SelectItem<ValueType>, 'value' | 'label' | 'disabled'>>

  /**
   * Currently selected value
   */
  value?: ValueType

  /**
   * Default value when uncontrolled
   */
  defaultValue?: ValueType

  /**
   * Callback when item changes. Called for both controlled and uncontrolled selects
   */
  onChange?: (value: ValueType) => void

  /**
   * Optional className for the ButtonGroup wrapper
   */
  className?: string

  /**
   * Set the button theme when it is selected
   */
  activeTheme?: ButtonThemeType

  /**
   * Size of the button
   *
   * @default "regular"
   */
  size?: ButtonSizeType
}

export function ButtonSwitch<ValueType>(props: Omit<ButtonSwitchProps<ValueType>, 'children'>) {
  const {
    items,
    value: controlledValue,
    label,
    defaultValue,
    onChange,
    className = 'flex p-0.5',
    size,
    labelClassNames,
    rounding = 'regular',
    activeTheme = 'blue',
    ...restProps
  } = props
  const [internalValue, setInternalValue] = React.useState<ValueType | undefined>(defaultValue)
  const value = controlledValue !== undefined ? controlledValue : internalValue

  const handleChange = React.useCallback(
    (newValue: ValueType) => {
      setInternalValue(newValue)
      onChange?.(newValue)
    },
    [onChange]
  )

  return (
    <div className="inline-flex items-center gap-1">
      <ButtonGroup {...restProps} rounding="regular" className={className} hasDivider={false}>
        {items.map((option, index) => (
          <Button
            key={`button-${index}`}
            contentClass="flex justify-center"
            size={size}
            type="button"
            rounding={rounding}
            disabled={option.disabled}
            theme={value === option.value ? activeTheme : undefined}
            onClick={() => handleChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </ButtonGroup>
      {label && <span className={classNames('select-none text-2xs', labelClassNames)}>{label}</span>}
    </div>
  )
}
